import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

interface SaleTimerProps {
  deadline: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onExpire?: () => void;
}

export const SaleTimer: React.FC<SaleTimerProps> = ({ deadline, size = 'sm', className = '', onExpire }) => {
  const [timeLeft, setTimeLeft] = useState<{ d: number; h: number; m: number; s: number } | null>(null);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = +new Date(deadline) - +new Date();
      
      if (difference > 0) {
        return {
          d: Math.floor(difference / (1000 * 60 * 60 * 24)),
          h: Math.floor((difference / (1000 * 60 * 60)) % 24),
          m: Math.floor((difference / 1000 / 60) % 60),
          s: Math.floor((difference / 1000) % 60),
        };
      } else {
        if (onExpire) onExpire();
        return null;
      }
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      const t = calculateTimeLeft();
      setTimeLeft(t);
      if (!t) clearInterval(timer);
    }, 1000);

    return () => clearInterval(timer);
  }, [deadline, onExpire]);

  if (!timeLeft) return null;

  const pad = (n: number) => n.toString().padStart(2, '0');

  // Smaller compact version
  if (size === 'sm') {
    return (
      <div className={`flex items-center gap-1.5 px-2 py-1 bg-red-500/10 border border-red-500/30 rounded text-red-400 font-mono text-[9px] font-bold ${className}`}>
        <Clock size={10} className="animate-pulse" />
        <span>{timeLeft.d}d {pad(timeLeft.h)}h {pad(timeLeft.m)}m</span>
      </div>
    );
  }

  // Larger version for Modals/Hero
  return (
    <div className={`flex items-center gap-3 ${className}`}>
        <div className="flex flex-col items-center">
            <div className="text-xl font-mono font-bold text-white bg-red-600 rounded px-2 py-1 min-w-[40px] text-center">{pad(timeLeft.d)}</div>
            <span className="text-[8px] uppercase font-bold text-red-400 mt-1">Days</span>
        </div>
        <span className="text-red-600 font-bold mb-3">:</span>
        <div className="flex flex-col items-center">
            <div className="text-xl font-mono font-bold text-white bg-red-600 rounded px-2 py-1 min-w-[40px] text-center">{pad(timeLeft.h)}</div>
            <span className="text-[8px] uppercase font-bold text-red-400 mt-1">Hrs</span>
        </div>
        <span className="text-red-600 font-bold mb-3">:</span>
        <div className="flex flex-col items-center">
            <div className="text-xl font-mono font-bold text-white bg-red-600 rounded px-2 py-1 min-w-[40px] text-center">{pad(timeLeft.m)}</div>
            <span className="text-[8px] uppercase font-bold text-red-400 mt-1">Min</span>
        </div>
        <span className="text-red-600 font-bold mb-3">:</span>
        <div className="flex flex-col items-center">
            <div className="text-xl font-mono font-bold text-white bg-red-600 rounded px-2 py-1 min-w-[40px] text-center">{pad(timeLeft.s)}</div>
            <span className="text-[8px] uppercase font-bold text-red-400 mt-1">Sec</span>
        </div>
    </div>
  );
};
